import React, { useEffect, useRef, useState, memo } from "react";

type SpotlightMaskProps = {
  zoom: number;
  socketRequest: any;
  /** circle: 直徑佔寬度%；strip: 高度佔高度% */
  holePercent: number;
  /** 參考點百分比（0~100）。stripPivot=center 時表示中心；top 表示上緣；bottom 表示下緣 */
  initialCenter?: { xPct: number; yPct: number };
  settings?: any;
  shape?: "circle" | "strip" | "square";
  /** 長條高度的基準：center=以中心延伸；top=以上緣延伸向下；bottom=以下緣延伸向上 */
  stripPivot?: "center" | "top" | "bottom";
  /** 將長條釘在容器上/下緣（不隨滑鼠移動）。預設 'none' */
  stripPinnedEdge?: "none" | "top" | "bottom";
  /** 舊參數（若你程式仍有帶）：center/top/bottom。會自動對應到上面兩個參數 */
  stripAnchor?: "center" | "top" | "bottom";

  /** actorId（老師ID） */
  actorId: string | null;
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const isSafariLike = () =>
  /iPad/.test(navigator.userAgent) ||
  (navigator.userAgent.includes("Mac") && "ontouchend" in document);

const Mask: React.FC<SpotlightMaskProps> = ({
  zoom,
  socketRequest,
  holePercent,
  initialCenter = { xPct: 50, yPct: 50 },
  settings,
  shape = "circle",
  stripPivot,
  stripPinnedEdge,
  stripAnchor, // 舊參數，會被下方轉換

  actorId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [centerPct, setCenterPct] = useState(initialCenter);
  const [isDragging, setIsDragging] = useState(false);

  // ★ props 新增 actorId（老師ID）
  const sessionRef = useRef<{ id: string; seq: number } | null>(null);
  const newSessionId = () =>
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  // ★ props 新增 actorId（老師ID）

  useEffect(() => {
    console.log("shape", shape);
  }, [shape]);

  // 舊 stripAnchor → 新語意：pin 表示釘住、pivot 表示可移動的基準
  const effectivePinned: "none" | "top" | "bottom" =
    stripPinnedEdge ??
    (stripAnchor === "top"
      ? "top"
      : stripAnchor === "bottom"
      ? "bottom"
      : "none");

  const effectivePivot: "center" | "top" | "bottom" =
    stripPivot ?? (stripAnchor ? (stripAnchor as any) : "center");

  const getScale = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const ow = el.offsetWidth || rect.width;
    const oh = el.offsetHeight || rect.height;
    return { rect, ow, oh, scaleX: rect.width / ow, scaleY: rect.height / oh };
  };

  const clientToUnscaledPercent = (
    el: HTMLElement,
    clientX: number,
    clientY: number,
  ) => {
    const { rect, ow, oh } = getScale(el);
    const ux = clientX - rect.left;
    const uy = clientY - rect.top;
    return {
      xPct: Math.max(0, Math.min(100, (ux / ow / zoom) * 100)),
      yPct: Math.max(0, Math.min(100, (uy / oh / zoom) * 100)),
      ux,
      uy,
      ow,
      oh,
    };
  };

  const getRadiusPx = () => {
    const w = containerRef.current?.clientWidth ?? 0;
    return (holePercent / 100) * w * 0.5;
  };

  const getCenterPx = () => {
    const el = containerRef.current;
    if (!el) return { cx: 0, cy: 0, w: 0, h: 0 };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const cx = (centerPct.xPct / 100) * w;
    const cy = (centerPct.yPct / 100) * h;
    return { cx, cy, w, h };
  };

  /**
   * strip 用：計算長條洞的 top/bottom（px）
   * - pinnedEdge=top/bottom：釘住容器邊（不可動）
   * - pivot=center：以中心延伸
   * - pivot=top：以上緣為基準（改高度只往下）
   * - pivot=bottom：以下緣為基準（改高度只往上）
   * py = 依照 pivot 意義儲存的百分比（我們用 centerPct.yPct 當「手把」）
   */
  const getStripBandPx = (w: number, h: number, py: number) => {
    const bandH = clamp((holePercent / 100) * h, 0, h);

    // 釘住容器邊的老行為
    if (effectivePinned === "top") {
      return { top: 0, bottom: bandH };
    }
    if (effectivePinned === "bottom") {
      return { top: h - bandH, bottom: h };
    }

    // 可移動的 pivot 模式
    if (effectivePivot === "top") {
      const top = clamp(py, 0, h);
      const bottom = Math.min(top + bandH, h);
      return { top, bottom: bottom };
    }
    if (effectivePivot === "bottom") {
      const bottom = clamp(py, bandH, h);
      return { top: bottom - bandH, bottom };
    }
    // center
    const topUnclamped = py - bandH / 2;
    const top = clamp(topUnclamped, 0, h - bandH);
    return { top, bottom: top + bandH };
  };

  const buildCircleMask = () => {
    const forward = settings?.mode === "forward";
    if (isSafariLike()) {
      const el = containerRef.current;
      if (!el) return {};
      const { ow, oh } = getScale(el);
      const r = (holePercent / 100) * ow * 0.5;
      if (r <= 0) {
        const none = forward
          ? "none"
          : "linear-gradient(transparent, transparent)";
        return { WebkitMaskImage: none, maskImage: none } as const;
      }
      const cx = (centerPct.xPct / 100) * ow;
      const cy = (centerPct.yPct / 100) * oh;
      const stop1 = `${r}px`;
      const stop2 = `${r + 1}px`;
      const mask = `radial-gradient(circle at ${cx}px ${cy}px, ${
        forward ? "transparent" : "#222222"
      } ${stop1}, ${forward ? "#222222" : "transparent"} ${stop2})`;
      return {
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskRepeat: "no-repeat" as const,
        maskRepeat: "no-repeat" as const,
      };
    } else {
      const r = getRadiusPx();
      if (r <= 0) {
        return {
          WebkitMaskImage:
            settings?.mode === "forward"
              ? "none"
              : "linear-gradient(transparent, transparent)",
          maskImage:
            settings?.mode === "forward"
              ? "none"
              : "linear-gradient(transparent, transparent)",
        } as const;
      }
      const { cx, cy } = getCenterPx();
      const stop1 = `${r}px`;
      const stop2 = `${r + 1}px`;
      const mask = `radial-gradient(circle at ${cx}px ${cy}px, ${
        settings?.mode === "forward" ? "transparent" : "#222222"
      } ${stop1}, ${
        settings?.mode === "forward" ? "#222222" : "transparent"
      } ${stop2})`;
      return {
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskRepeat: "no-repeat" as const,
        maskRepeat: "no-repeat" as const,
      };
    }
  };

  const buildStripMask = () => {
    const forward = settings?.mode === "forward";
    const OPAQUE = "#222222";
    const CLEAR = "transparent";

    if (isSafariLike()) {
      const el = containerRef.current;
      if (!el) return {};
      const { ow, oh } = getScale(el);
      const py = (centerPct.yPct / 100) * oh; // 依 pivot 語意：top=上緣、center=中心、bottom=下緣
      const { top, bottom } = getStripBandPx(ow, oh, py);
      if (bottom - top <= 0) {
        const none = forward
          ? "none"
          : "linear-gradient(transparent, transparent)";
        return { WebkitMaskImage: none, maskImage: none } as const;
      }
      const a = forward ? OPAQUE : CLEAR;
      const b = forward ? CLEAR : OPAQUE;
      const mask = `linear-gradient(
        to bottom,
        ${a} 0px,
        ${a} ${top}px,
        ${b} ${top}px,
        ${b} ${bottom}px,
        ${a} ${bottom}px,
        ${a} 100%
      )`;
      return {
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskRepeat: "no-repeat" as const,
        maskRepeat: "no-repeat" as const,
      };
    } else {
      const el = containerRef.current;
      if (!el) return {};
      const w = el.clientWidth;
      const h = el.clientHeight;
      const py = (centerPct.yPct / 100) * h; // 依 pivot 語意
      const { top, bottom } = getStripBandPx(w, h, py);
      if (bottom - top <= 0) {
        return {
          WebkitMaskImage:
            settings?.mode === "forward"
              ? "none"
              : "linear-gradient(transparent, transparent)",
          maskImage:
            settings?.mode === "forward"
              ? "none"
              : "linear-gradient(transparent, transparent)",
        } as const;
      }
      const a = settings?.mode === "forward" ? "#222222" : "transparent";
      const b = settings?.mode === "forward" ? "transparent" : "#222222";
      const mask = `linear-gradient(
        to bottom,
        ${a} 0px,
        ${a} ${top}px,
        ${b} ${top}px,
        ${b} ${bottom}px,
        ${a} ${bottom}px,
        ${a} 100%
      )`;
      return {
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskRepeat: "no-repeat" as const,
        maskRepeat: "no-repeat" as const,
      };
    }
  };

  const buildSquareMask = () => {
    const forward = settings?.mode === "forward";

    const buildFrom = (w: number, h: number, cx: number, cy: number) => {
      const side = (holePercent / 100) * w; // 邊長依寬度百分比
      if (side <= 0) {
        if (forward)
          return { WebkitMaskImage: "none", maskImage: "none" } as const;
        return {
          WebkitMaskImage: "linear-gradient(transparent, transparent)",
          maskImage: "linear-gradient(transparent, transparent)",
        } as const;
      }
      const half = side / 2;
      let left = cx - half;
      let right = cx + half;
      let top = cy - half;
      let bottom = cy + half;

      // 夾到容器內
      left = clamp(left, 0, w);
      right = clamp(right, left, w);
      top = clamp(top, 0, h);
      bottom = clamp(bottom, top, h);

      const wL = left;
      const wR = w - right;
      const hT = top;
      const hB = h - bottom;
      const cW = Math.max(0, right - left);
      const cH = Math.max(0, bottom - top);

      // 組裝多層屬性（只加入 >0 的層，避免 0 尺寸引擎抖動）
      const imgs: string[] = [];
      const poss: string[] = [];
      const sizes: string[] = [];
      const reps: string[] = [];

      const push = (img: string, pos: string, size: string) => {
        imgs.push(img);
        poss.push(pos);
        sizes.push(size);
        reps.push("no-repeat");
      };

      if (forward) {
        // 上
        if (hT > 0)
          push("linear-gradient(#000,#000)", `0px 0px`, `${w}px ${hT}px`);
        // 下
        if (hB > 0)
          push(
            "linear-gradient(#000,#000)",
            `0px ${bottom}px`,
            `${w}px ${hB}px`,
          );
        // 左
        if (wL > 0)
          push("linear-gradient(#000,#000)", `0px 0px`, `${wL}px ${h}px`);
        // 右
        if (wR > 0)
          push(
            "linear-gradient(#000,#000)",
            `${right}px 0px`,
            `${wR}px ${h}px`,
          );

        if (imgs.length === 0) {
          return {
            opacity: 0,
          } as const;
        }

        const image = imgs.join(", ");
        const position = poss.join(", ");
        const size = sizes.join(", ");
        const repeat = reps.join(", ");

        return {
          WebkitMaskImage: image,
          maskImage: image,
          WebkitMaskPosition: position,
          maskPosition: position,
          WebkitMaskSize: size,
          maskSize: size,
          WebkitMaskRepeat: repeat,
          maskRepeat: repeat,
        } as const;
      } else {
        // 反相：只畫中間正方形
        if (cW <= 0 || cH <= 0) {
          return {
            WebkitMaskImage: "linear-gradient(transparent, transparent)",
            maskImage: "linear-gradient(transparent, transparent)",
          } as const;
        }
        const image = "linear-gradient(#000,#000)";
        const position = `${left}px ${top}px`;
        const size = `${cW}px ${cH}px`;
        const repeat = "no-repeat";
        return {
          WebkitMaskImage: image,
          maskImage: image,
          WebkitMaskPosition: position,
          maskPosition: position,
          WebkitMaskSize: size,
          maskSize: size,
          WebkitMaskRepeat: repeat,
          maskRepeat: repeat,
        } as const;
      }
    };

    if (isSafariLike()) {
      const el = containerRef.current;
      if (!el) return {};
      const { ow, oh } = getScale(el);
      const cx = (centerPct.xPct / 100) * ow;
      const cy = (centerPct.yPct / 100) * oh;
      return buildFrom(ow, oh, cx, cy);
    } else {
      const el = containerRef.current;
      if (!el) return {};
      const w = el.clientWidth;
      const h = el.clientHeight;
      const cx = (centerPct.xPct / 100) * w;
      const cy = (centerPct.yPct / 100) * h;
      return buildFrom(w, h, cx, cy);
    }
  };

  const maskStyle = () => {
    if (shape === "strip") return buildStripMask();
    if (shape === "square") return buildSquareMask(); // ← 新增
    return buildCircleMask();
  };

  const sendSettingsToSocket = (
    phase: "begin" | "move" | "end",
    payload: any,
    holePercentSetting: boolean,
  ) => {
    const now = Date.now();
    const s = sessionRef.current ?? { id: undefined, seq: -1 };
    (async () => {
      await socketRequest("updateBoard", {
        coverData: {
          ...payload,
          actorId, // ← 誰在操作
          sessionId: s.id, // ← 一次拖曳同一個
          seq: s.seq, // ← 當前序號
          phase, // ← begin/move/end
          ts: now, // ← 時間戳
          holePercentSetting,
        },
      });
    })();
  };

  const onMouseDownOverlay = (e: any) => {
    draggingRef.current = true;
    e.preventDefault();

    sessionRef.current = { id: newSessionId(), seq: 0 };
    sendSettingsToSocket(
      "begin",
      {
        centerPct: centerPct,
        type: settings?.type,
        holePercent,
        shape,
        stripPivot: effectivePivot,
        stripPinnedEdge: effectivePinned,
      },
      false,
    );

    const onMove = (ev: MouseEvent | TouchEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      if (!sessionRef.current) return;
      const event = ev instanceof MouseEvent ? ev : ev.touches[0];
      sessionRef.current.seq += 1;

      if (isSafariLike()) {
        const el = containerRef.current!;
        const next = clientToUnscaledPercent(el, event.clientX, event.clientY);
        const nextX = shape === "strip" ? 50 : next.xPct; // strip：鎖 X=50
        const nextY = next.yPct; // Y 可動
        setCenterPct({ xPct: nextX, yPct: nextY });
        sendSettingsToSocket(
          "begin",
          {
            centerPct: { xPct: nextX, yPct: nextY },
            type: settings?.type,
            holePercent,
            shape,
            stripPivot: effectivePivot,
            stripPinnedEdge: effectivePinned,
          },
          false,
        );
      } else {
        const rect = containerRef.current.getBoundingClientRect();
        const x = clamp(event.clientX - rect.left, 0, rect.width);
        const y = clamp(event.clientY - rect.top, 0, rect.height);
        const xPct = (x / rect.width) * 100;
        const yPct = (y / rect.height) * 100;
        const nextX = shape === "strip" ? 50 : xPct;
        const nextY = yPct;
        setCenterPct({ xPct: nextX, yPct: nextY });
        sendSettingsToSocket(
          "begin",
          {
            centerPct: { xPct: nextX, yPct: nextY },
            type: settings?.type,
            holePercent,
            shape,
            stripPivot: effectivePivot,
            stripPinnedEdge: effectivePinned,
          },
          false,
        );
      }

      setIsDragging(true);
    };

    const onUp = () => {
      if (sessionRef.current) {
        sessionRef.current = null;
      }
      draggingRef.current = false;
      setIsDragging(false);
      document.onpointerup = null;
      document.onpointermove = null;
      document.onmouseup = null;
      document.onmousemove = null;
      document.ontouchend = null;
      document.ontouchmove = null;
    };

    document.onpointerup = onUp;
    document.onpointermove = onMove;
    document.onmouseup = onUp;
    document.onmousemove = onMove;
    document.ontouchend = onUp;
    document.ontouchmove = onMove;
  };

  // 夾限 + 狀態同步
  useEffect(() => {
    if (sessionRef.current) {
      sessionRef.current.seq += 1;
    }
    setCenterPct(p => ({
      xPct: clamp(shape === "strip" ? 50 : p.xPct, 0, 100), // strip 永遠置中滿版
      yPct: clamp(p.yPct, 0, 100),
    }));
    sendSettingsToSocket(
      "begin",
      {
        centerPct: centerPct,
        holePercent,
        type: settings?.type,
        shape,
        stripPivot: effectivePivot,
        stripPinnedEdge: effectivePinned,
      },
      true,
    );
  }, [holePercent, shape, effectivePivot, effectivePinned]);

  useEffect(() => {
    if (sessionRef.current) {
      sessionRef.current.seq += 1;
    }
    sendSettingsToSocket(
      "begin",
      {
        centerPct: centerPct,
        mode: settings?.mode || "forward",
        type: settings?.type,
        holePercent,
        shape,
        stripPivot: effectivePivot,
        stripPinnedEdge: effectivePinned,
      },
      true,
    );
  }, [settings]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 4,
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <div
        onPointerDown={onMouseDownOverlay}
        className={isDragging ? "spotlight-drag" : ""}
        style={{
          position: "absolute",
          inset: 0,
          background: "#222222",
          pointerEvents: "auto",
          ...maskStyle(),
        }}
      />
    </div>
  );
};

export default memo(Mask);
